import { useState } from 'react';
import { Product, State } from 'lib/enums';
import useAsyncEffect from 'lib/frontend/hooks/useAsyncEffect';
import useDebounce from 'lib/frontend/hooks/useDebounce';
import {
  AdjudicationFilters,
  searchPublisherOrganizations
} from 'routes/placeScroll/helpers/publisherSearch';

export type PublisherFilters = {
  restrictedSingleState: number | undefined;
  isUserPublisher: boolean;
  restrictedPublisherIds: string[];
  product: Product;
  adjudicationFilters?: AdjudicationFilters;
  fetchAllOrganizations?: boolean;
  showOnlyColumnCertified?: boolean;
};

export function useFetchAvailablePublishers({
  restrictedSingleState,
  isUserPublisher,
  restrictedPublisherIds,
  product,
  adjudicationFilters,
  fetchAllOrganizations = false,
  showOnlyColumnCertified = false
}: PublisherFilters) {
  const [stateFilter, setStateFilter] = useState<number | undefined>(
    restrictedSingleState
  );
  const [searchText, setSearchText] = useState('');
  const [columnCertifiedFilter, setColumnCertifiedFilter] = useState(
    showOnlyColumnCertified
  );
  const debouncedSearchText = useDebounce(searchText, 750);
  // If we're in a state restricted flow, we only show publishers in that state
  const activeStateFilter = restrictedSingleState || stateFilter;

  let stateOptions = [] as number[];

  const fetchData = async () => {
    const searchOptions = {
      isUserPublisher,
      stateFilter: activeStateFilter,
      restrictedPublisherIds,
      product,
      adjudicationFilters,
      showOnlyColumnCertified: columnCertifiedFilter
    };
    // NOTE: `searchPublisherOrganizations` handles error logging internally and returns empty lists on a failed request
    const { states, publisherOrganizations } =
      await searchPublisherOrganizations({
        ...searchOptions,
        search: debouncedSearchText,
        pageSize: 10,
        fetchAllOrganizations
      });

    // Only set the available states on the first load
    if (!stateOptions.length) {
      stateOptions = !restrictedSingleState
        ? State.items()
            .map(state => state.value)
            .sort((a, b) => a - b)
        : states.sort((a, b) => a - b);
    }

    return { stateOptions, sortedPublisherOptions: publisherOrganizations };
  };

  const { value, isLoading } = useAsyncEffect({
    fetchData,
    dependencies: [
      debouncedSearchText,
      isUserPublisher,
      activeStateFilter,
      columnCertifiedFilter,
      adjudicationFilters?.stateFilter?.id,
      adjudicationFilters?.countyFilter?.id,
      adjudicationFilters?.cityFilter?.id
    ]
  });

  stateOptions = value?.stateOptions ?? [];

  const publisherOptions = value?.sortedPublisherOptions ?? [];

  return {
    loading: isLoading,
    stateOptions,
    stateFilter,
    setStateFilter,
    publisherOptions,
    searchText,
    setSearchText,
    columnCertifiedFilter,
    setColumnCertifiedFilter
  };
}
